import loaderSignal from 'signals/Loader.signal';
import history from '../../../history';
import api from 'api/api';
import alertSignal from 'signals/Alert.signal';
import $globalSearchResults from './globalSearchResults.signal';
import { handleGlobalSearch } from 'libs/searchNavigation';
import { enumCaseToTabCase } from 'libs/string';
import formatMoney from 'utils/formatMoney';

export const RECORD_FIELDS = {
  'Funding Program': (record, searchResult) =>
    record.fundingProgramMembership?.fundingProgram?.name || record.fundingProgram?.name || record.fund?.fundingProgramMembership?.fundingProgram?.name,
  Lender: (record, searchResult) => record.fundingProgramMembership?.lender?.name || record.lender?.name || null,
  Business: (record, searchResult) => record.business?.name,
  Status: (record, searchResult) => enumCaseToTabCase(record.status),
  'Total Loan Amount': (record, searchResult) => {
    if (record.askedAmount !== undefined && record.askedAmount !== null) {
      return formatMoney(record.askedAmount);
    }
    if (record.allocationAsk !== undefined && record.allocationAsk !== null) {
      return formatMoney(record.allocationAsk);
    }
    return null;
  },
  'Enrolled Amount': (record, searchResult) => record.approvedAmount ? formatMoney(record.approvedAmount) : null,
  'LOA ID': (record, searchResult) => searchResult?.loaId || null,
  'CalCAP LOAN #': (record, searchResult) => searchResult?.calcapLoanNumber || null,
  'LENDER LOAN #': (record, searchResult) => searchResult?.lenderLoanNumber || null,
  'CSP App #': (record, searchResult) => searchResult?.csiId || null,
  Name: (record, searchResult) => record.name || record.fundName,
  EIN: (record, searchResult) => record.ein || record.misc?.einFromImport,
  'Provider Type': (record, searchResult) => enumCaseToTabCase(record.lenderType),
  'Contact Name': (record, searchResult) => record.contactName,
  'Email': (record, searchResult) => record.email || record.contactEmail,
  'Description': (record, searchResult) =>
    record.description ||
    record.overview ||
    'No description available.',
  'Phone Number': (record, searchResult) => record.phoneNumber,
  'Fund Size': (record, searchResult) => record.maxFundSize,
  'VC': (record, searchResult) => record.fundingProgramMembership?.ventureCapitalFirm?.name || record.ventureCapitalFirm?.name || record.fund?.fundingProgramMembership?.ventureCapitalFirm?.name || null,
  'Platform': (record, searchResult) => record.platform?.name,
  'Program Type': (record, searchResult) => record.referenceProgram?.programType?.programTypeName,
  'Total Funding': (record, searchResult) => formatMoney(record.ceiling),
  'Fund': (record, searchResult) => record.fund.fundName,
  'Amount': (record, searchResult) => formatMoney(record.capitalCallAmount),
};

export const RECORD_TYPE_CATEGORY_ORDER = [
  'Application',
  'Business',
  'CapitalCall',
  'Fund',
  'FundingProgram',
  'Lender',
  'ProgramRequest',
  'VentureCapitalFirm',
];

export const highlightSearchTerms = (text, searchQuery) => {
  if (!searchQuery || !text || typeof text !== 'string') {
    return text;
  }
  const escapedQuery = searchQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const searchTerms = escapedQuery.trim().split(/\s+/).filter(term => term.length > 0);
  if (searchTerms.length === 0) return text;
  const pattern = new RegExp(`(${searchTerms.join('|')})`, 'gi');
  const parts = text.split(pattern);
  return parts.map((part, index) => {
    const isMatch = searchTerms.some(term => part.toLowerCase() === term.toLowerCase());
    if (isMatch) {
      return (
        <mark key={index} className="search-highlight">
          {part}
        </mark>
      );
    }
    return part;
  });
};

export const sortSearchResultsByCategory = (results) => {
  if (!results?.length) return results;
  const groups = {};
  results.forEach((r) => {
    const type = r.recordType || 'Other';
    if (!groups[type]) groups[type] = [];
    groups[type].push(r);
  });
  const ordered = [];
  RECORD_TYPE_CATEGORY_ORDER.forEach((type) => {
    if (groups[type]) ordered.push(...groups[type]);
  });
  Object.keys(groups)
    .filter((t) => !RECORD_TYPE_CATEGORY_ORDER.includes(t))
    .sort()
    .forEach((type) => ordered.push(...groups[type]));
  return ordered;
};

export const RECORD_FIELDS_TO_RENDER = {
  Application: [
    'Funding Program',
    'Lender',
    'Business',
    'Status',
    'Total Loan Amount',
    'Enrolled Amount',
    'LOA ID',
    'CalCAP LOAN #',
    'LENDER LOAN #',
    'CSP App #',
  ],
  ProgramRequest: [
    'VC',
    'Lender',
    'Funding Program',
    'Asked Amount',
    'Status',
  ],
  FundingProgram: [
    'Name',
    'Program Type',
    'Platform',
    'Description',
    'Total Funding',
  ],
  VentureCapitalFirm: [
    'Name',
    'EIN',
    'Contact Name',
    'Email',
    'Provider Type',
    'Description',
  ],
  Lender: [
    'Name',
    'EIN',
    'Contact Name',
    'Email',
    'Provider Type',
    'Description',
  ],
  Business: [
    'Name',
    'EIN',
    'Contact Name',
    'Email',
    'Phone Number',
    'Description',
  ],
  Fund: [
    'Name',
    'VC',
    'Funding Program',
    'Fund Size',
  ],
  CapitalCall: [
    'Fund',
    'Funding Program',
    'VC',
    'Amount',
    'Status',
  ],
};

export const fetchAndSetSearchResults = async () => {
  $globalSearchResults.update({ isLoading: true });
  try {
    loaderSignal.update({ isContentLoading: true });
    const params = Object.fromEntries(new URLSearchParams(history.location.search));
    const searchResults = await api.get({
      path: '/search',
      options: {
        searchQuery: params.query ?? '',
        limit: 100,
      },
    });

    $globalSearchResults.update({ searchResults, query: params.query ?? '' });
  } catch (error) {
    alertSignal.update({
      type: 'notification',
      message: error.message,
    });
  } finally {
    $globalSearchResults.update({ isLoading: false });
    loaderSignal.reset();
  }
};

export const handleGlobalSearchChange = (e) => $globalSearchResults.update({
  query: e.value,
});

export const handleEnterKeyDown = (e) => {
  if (e.key === 'Enter') {
    handleGlobalSearch($globalSearchResults.value.query);
  }
};
